import { crudPath, request } from '@/util/request';
import { message } from 'ant-design-vue';
import { computed, inject, reactive, watch } from 'vue';
import { ScForm } from '@/models/ScForm';
import { serviceManager } from '@/components/business/crud/service/ServiceManager';
import _ from 'lodash';
import { ModelWidgetSchema } from '@/components/business/crud/interface/ModelWidgetSchema';
import { FormWidgetContainer } from '@/components/business/crud/widgets/FormWidgetContainer';
import { WindowTool } from '@/components/common/window/WindowInterface';
import { app } from '@/models/App';
import { WidgetStatus } from '@/components/business/crud/interface/WidgetType';

export interface CrudFormParam {
  primary?: string;
  status?: WidgetStatus;
}

interface CrudFormState {
  primary?: string;
  // 只在创建时生效
}

export class CrudFormService {
  protected schema: ModelWidgetSchema;
  protected widgetContainer: FormWidgetContainer;
  protected windowTool = inject<WindowTool | false>(WindowTool, false);
  protected form: ScForm;
  protected readonly state: CrudFormState;
  protected readonly isCreate = computed(() => !this.state.primary);

  /**
   * 创建实例
   * @param moduleName 模块名
   * @param controllerName 控制器名
   * @param params
   */
  static create(
    moduleName: string,
    controllerName: string,
    params?: CrudFormParam
  ) {
    const Clazz = serviceManager.getService(
      moduleName,
      controllerName,
      'form',
      CrudFormService
    );
    const instance = new Clazz(moduleName, controllerName, params);
    instance.init();
    return instance;
  }

  protected constructor(
    protected moduleName: string,
    protected controllerName: string,
    params?: CrudFormParam
  ) {
    this.schema = app.data.widgetMap[moduleName][controllerName];
    this.widgetContainer = new FormWidgetContainer();
    this.state = reactive({
      primary: params?.primary,
    });
    if (this.isCreate.value) {
      this.widgetContainer.status.value = 'edit';
      this.widgetContainer.canToggle.value = false;
    } else {
      this.widgetContainer.status.value = params?.status || 'edit';
      this.widgetContainer.canToggle.value =
        (this.widgetContainer.status.value === 'edit' && this.schema.view) ||
        (this.widgetContainer.status.value === 'show' && this.schema.update);
    }
    Object.entries(this.schema.widgets).map(([propName, widget]) => {
      this.widgetContainer.addField(propName, widget.title, widget.type, {
        groupName: widget.groupName,
        desc: widget.desc,
      });
    });
    this.form = new ScForm();
    // 跟随场景调整字段状态，使用的是schema值 todo 子类设置的值会被覆盖
    watch(
      this.isCreate,
      () => {
        Object.entries(this.widgetContainer.fields.value).map(([id, field]) => {
          if (this.isCreate.value) {
            field.statusSettings.edit = this.schema.widgets[id].create;
          } else {
            field.statusSettings.edit = this.schema.widgets[id].update;
            field.statusSettings.show = this.schema.widgets[id].view;
          }
        });
      },
      { immediate: true }
    );
  }

  /**
   * 设置只读字段
   * 将值设置为表单默认值，并将字段设置为show
   * @param param
   */
  setReadonlyFields(param?: Record<string, unknown>) {
    if (!param) {
      return;
    }
    for (const field of Object.values(this.widgetContainer.fields.value)) {
      if (Reflect.has(param, field.propName)) {
        this.form.origin.value[field.propName] = param[field.propName];
        this.form.data.value[field.propName] = param[field.propName];
        if (field.statusSettings.edit === 'edit') {
          field.statusSettings.edit = 'show';
        }
      }
    }
  }

  /**
   * 初始化
   * 属性配置相关初始化放在构造函数，方便子类进行修改
   * 请求数据，创建监听逻辑放在init, 1.方便子类调用或覆盖；2.方便外部调用时，先完成配置再进行监听及请求数据
   */
  async init() {
    await this.form.init(() => this.getOriginData());
  }

  use() {
    return {
      title: computed(() =>
        this.isCreate.value
          ? '创建' + this.schema.title
          : this.widgetContainer.status.value === 'edit'
          ? '编辑' + this.schema.title
          : this.schema.title + '详情'
      ),
      submit: this.submit.bind(this),
      form: this.form,
      ...this.widgetContainer.use(),
    };
  }

  /**
   * 获取原数据
   * @protected
   */
  protected async getOriginData() {
    if (this.isCreate.value) {
      return this.getCreateData();
    } else {
      return this.getUpdateViewData();
    }
  }

  protected async getUpdateViewData() {
    const origin = await request(
      crudPath(this.moduleName, this.controllerName, 'view'),
      {
        [this.schema.primary]: this.state.primary,
        load: this.getLoadProps(),
      }
    );
    if (!origin) {
      throw new Error('此' + this.schema.title + '不存在');
    }
    const widgetItemMap = this.widgetContainer.widgetItemMap;
    const morphList: {
      [indexPath: string]: {
        ids: { [id: string]: { name: string }[] };
        labels: string[];
      };
    } = {};
    Object.entries(widgetItemMap.value).map(([name, i]) => {
      const widget = i.props.widget;
      switch (widget.name) {
        case 'morph':
          {
            if (!origin[name]) {
              return;
            }
            const modelName = origin[widget.modelNameProp];
            if (!modelName) {
              return;
            }
            const model = widget.models[modelName];
            if (!model) {
              return;
            }
            if (!morphList[model.basePath]) {
              morphList[model.basePath] = { ids: {}, labels: [] };
            }
            const label = model.labelPropName || 'name';
            morphList[model.basePath].labels.push(label);
            if (!morphList[model.basePath].ids[origin[name]]) {
              morphList[model.basePath].ids[origin[name]] = [];
            }
            morphList[model.basePath].ids[origin[name]].push({ name });
          }
          break;
      }
    });
    const tasks = Object.entries(morphList).map(async ([path, item]) => {
      const res = await request(path + '/index', {
        condition: { id: { $in: Object.keys(item.ids) } },
        load: _.uniq(['id'].concat(item.labels)),
        limit: Object.keys(item.ids).length,
        offset: 0,
      });
      Object.entries(item.ids).map(([id, rows]) => {
        const data = res.list.find((i: any) => i.id === id);
        rows.map((index) => {
          origin[index.name] = data;
        });
      });
    });
    await Promise.all(tasks);
    return origin;
  }

  protected async getCreateData() {
    const origin: Record<string, unknown> = {};
    for (const field of Object.values(this.widgetContainer.fields.value)) {
      const defaultValue = this.schema.widgets[field.propName].default;
      if (defaultValue !== undefined) {
        origin[field.propName] = defaultValue;
      }
    }
    return origin;
  }

  protected getLoadProps(loadProps: any[] = []) {
    const widgetItemMap = this.widgetContainer.widgetItemMap;
    Object.values(widgetItemMap.value).map((i) => {
      switch (i.props.widget.name) {
        case 'selectRemote':
          loadProps.push({
            propName: i.props.propName,
            load: [
              i.props.widget.labelFieldName,
              i.props.widget.valueFieldName,
            ],
          });
          break;
        case 'hasMany':
          break;
        default:
          loadProps.push(i.props.propName);
      }
    });
    _.uniqWith(loadProps, (a, b) => _.isEqual(a, b));
    return loadProps;
  }

  protected async submit() {
    const res = await this.doSubmit();
    if (res) {
      // 如果提交成功，将当前表单数据保存为原始数据，用于重置
      this.form.saveOrigin();
      message.success(this.schema.title + '保存成功');
      if (this.windowTool) {
        this.windowTool.close(res);
      }
      return res;
    }
  }

  /**
   * 提交表单
   * @protected
   */
  protected doSubmit() {
    if (this.isCreate.value) {
      return request(
        crudPath(this.moduleName, this.controllerName, 'create'),
        this.processSubmitData(this.form.data.value)
      );
    } else {
      const origin = this.processSubmitData(this.form.origin.value);
      const current = this.processSubmitData(this.form.data.value);
      const data: any = {};
      for (const key in current) {
        if (!_.isEqual(origin[key], current[key])) {
          data[key] = current[key];
        }
      }
      if (Object.keys(data).length > 0) {
        return request(
          crudPath(this.moduleName, this.controllerName, 'update'),
          {
            [this.schema.primary]: this.state.primary,
            data,
          }
        );
      } else {
        return true;
      }
    }
  }

  protected processSubmitData(formData: Record<string, unknown>) {
    const submitData: Record<string, unknown> = {};
    for (const field of Object.values(this.widgetContainer.fields.value)) {
      switch (field.widget.name) {
        case 'selectRemote':
          if (field.widget.multi) {
            const valueFieldName = field.widget.valueFieldName;
            submitData[field.propName] = formData[field.propName]
              ? {
                  sync: (formData[field.propName] as any).list.map(
                    (i: any) => i[valueFieldName]
                  ),
                }
              : { sync: [] };
          } else {
            submitData[field.propName] =
              formData[field.propName] === null
                ? null
                : _.get(formData, [
                    field.propName,
                    field.widget.valueFieldName,
                  ]);
          }
          break;
        case 'morph':
          submitData[field.propName] =
            formData[field.propName] === null
              ? null
              : _.get(formData, [field.propName, 'id']);
          break;
        default:
          submitData[field.propName] = formData[field.propName];
      }
    }
    return submitData;
  }
}
